import { DateRange } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { AttributeValue, CustomAttribute, Site, User } from 'src/app/models/user';
import { Program } from 'src/app/models/program';
import { DateRangePickerSelectionNoLabelPipe } from 'src/app/pipes/data-range-picker-selection-no-label.pipe';
import { PersonaEntry } from 'src/app/services/personas.service';
import { Collaboration } from '@pncl/common-models';
import { Event } from 'src/app/models/event';
import { ResourceItemModel } from 'src/app/models/resource';
import { intersectionBy } from 'lodash';
import { DatepickerRange } from '../date-range-picker/date-range-picker.component';

export interface IFilterPill {
  key: FilterKey;
  tag: string;
  displayText: string;
  isLocked: boolean;
}

export enum FilterType {
  SiteSearch = 'site-search',
  UserSearch = 'user-search',
  Checkbox = 'checkbox',
  Date = 'date',
  MultiSelect = 'multi-select',
  Role = 'role',
  SpaceSearch = 'space-search',
}

export interface SpaceItem {
  _id: string;
  title: string;
  owner: User;
}

// these keys should be the keys used for calling backend endpoint with filters
export enum DefaultFilterKey {
  SitesFilter = 'sites',
  ParticipantsFilter = 'participants',
  HostsFilter = 'hosts',
  OwnerFilter = 'owner',
  DateCreatedFilter = 'createdRange',
  DateUpdatedFilter = 'dateRange',
  AttributesFilter = 'customAttributes',
  RolesFilter = 'roles',
  ArchiveStatusFilter = 'archiveStatus',
  EventsAtSameTimeFilter = 'eventsAtSameTime',
  SpacesFilter = 'spaces',
  UsersFilter = 'users',
  EventOrganizersFilter = 'organizers',
  EventAttendeesFilter = 'attendees',
  ProgramsFilter = 'programs',
  FileTypesFilter = 'type',
  HasCallsFilter = 'hasCalls',
  HasRecordingFilter = 'hasRecording',
}

export type FilterKey = DefaultFilterKey | string;

// Each pair declares a relationship between filters whose addable lists should react to each other's selections.
// For example, selecting sites narrows the programs list, and selecting programs narrows the sites list.
export const LINKED_FILTER_KEYS: Map<FilterKey, FilterKey> = new Map([
  [DefaultFilterKey.SitesFilter, DefaultFilterKey.ProgramsFilter],
  [DefaultFilterKey.ProgramsFilter, DefaultFilterKey.SitesFilter],
]);

export type UserSearchFilterKey =
  | DefaultFilterKey.HostsFilter
  | DefaultFilterKey.ParticipantsFilter
  | DefaultFilterKey.UsersFilter
  | DefaultFilterKey.EventOrganizersFilter
  | DefaultFilterKey.EventAttendeesFilter;

export interface IFilterAPI {
  query?: string;
  search?: string;

  sortBy?: string;

  sites?: string[];
  hosts?: string[];
  participants?: string[];
  users?: string[];
  owner?: string;
  dateRange?: DateRange<Date | null>;
  createdRange?: DateRange<Date | null>;
  customAttributes?: string[];
  institution?: string;
  spaces?: string[];
  timeRangeOverlap?: DateRange<Date | null>;

  // profiles API keys
  roles?: string[];
  archiveStatus?: Collaboration.Spaces.ArchiveStatus;

  // Events API keys
  organizers?: string[];
  attendees?: string[];
  programs?: string[];

  // Files API keys
  type?: string[];

  hasCalls?: boolean;
  hasRecording?: boolean;
}

export abstract class AbstractBaseFilter {
  abstract key: FilterKey;
  abstract type: FilterType;

  abstract isApplied(): boolean;
  abstract convertToAPIFormat(apiFilters: IFilterAPI): void;
  abstract handleFilterSelection(filterValue?: any): boolean;
  abstract handleClearFilter(): void;
  abstract getFilterPillText(): string;

  title = '';
  isLocked = false;

  // Override in child classes as needed for custom pill tag text
  getFilterPillTag(): string {
    return this.title;
  }

  getFilterPill(): IFilterPill {
    return {
      key: this.key,
      tag: this.getFilterPillTag(),
      displayText: this.getFilterPillText(),
      isLocked: this.isLocked,
    };
  }

  // Override in child classes when the incoming value needs to be resolved before being used for selection.
  // For example, the multi-select component compares selected vs addable items by object
  // reference (Set.has), so foreign objects (e.g. from initialFilters)
  // must be swapped for the exact references in _addableList before storage.
  resolveValue(value: any): any {
    return value;
  }

  // Override in child classes that need to react to linked filter selections.
  onLinkedFilterChanged(sourceFilter: FilterData): void {}
}

export abstract class AbstractMultiSelectFilter<
  T extends Record<string, any>,
> extends AbstractBaseFilter {
  // Public properties to specify the object fields to be used as key and value for the multi-select filter.
  // This is useful when the items in this filter's arrays are objects that does not necessarily have an _id or value field.
  // keyPropName field is used mainly for comparison purposes, while valuePropName field is used for display purposes.
  public keyPropName: string = '_id';
  public valuePropName: string = 'value';

  protected _allList: T[] = [];
  protected _addableList: T[] = [];
  protected _selectedList: T[] = [];
  protected translateService: TranslateService;

  get allList(): T[] {
    return this._allList;
  }

  get addableList(): T[] {
    return this._addableList;
  }

  get selectedList(): T[] {
    return this._selectedList;
  }

  constructor(translateService: TranslateService) {
    super();
    this.translateService = translateService;
  }

  override resolveValue(value: T[]): T[] {
    return intersectionBy(this._addableList, value, this.keyPropName);
  }

  override isApplied(): boolean {
    return !!this._selectedList.length;
  }

  override handleFilterSelection(filterValue?: T[]): boolean {
    this._selectedList = filterValue ?? [];
    return this.isApplied();
  }

  override handleClearFilter(): void {
    this._selectedList = [];
  }

  override getFilterPillText(): string {
    if (!this.selectedList.length) {
      return '';
    }

    return getMultiSelectFilterPillText(
      this.selectedList.length,
      this.selectedList[0][this.valuePropName],
      this.translateService,
    );
  }
}

export interface FilterKeyValuePair {
  key: FilterKey;
  value: any;
}

export function getMultiSelectFilterPillText(
  selectedItemsCount: number,
  firstItemName: string,
  translateService: TranslateService,
): string {
  const anyOfText = translateService.instant('Any of');
  const andText = translateService.instant('and');
  const otherText = translateService.instant(selectedItemsCount === 2 ? 'other' : 'others');

  const displayText =
    selectedItemsCount === 1
      ? `${firstItemName}`
      : `${anyOfText} "${firstItemName}" ${andText} ${selectedItemsCount - 1} ${otherText}`;

  return displayText;
}

export class SiteSearchFilter extends AbstractMultiSelectFilter<Site> {
  key: DefaultFilterKey.SitesFilter = DefaultFilterKey.SitesFilter;
  type = FilterType.SiteSearch;
  valuePropName = 'name';

  constructor(
    translateService: TranslateService,
    title: string,
    sites?: Site[],
    allSites?: Site[],
  ) {
    super(translateService);
    this.title = title;
    this._allList = allSites ?? [];
    this._selectedList = sites ?? [];
    this._addableList = [...this._allList];
  }

  override onLinkedFilterChanged(sourceFilter: FilterData): void {
    if (sourceFilter.key === DefaultFilterKey.ProgramsFilter) {
      const programFilter = sourceFilter as ProgramSearchFilter;
      const selectedProgramSiteIds = new Set(
        programFilter.selectedList.map((program) => program.siteId),
      );
      // Note that no selected programs means the program filter was cleared, so reset this filter's options to show all unfiltered options again.
      this._addableList = selectedProgramSiteIds.size
        ? this._allList.filter((site) => selectedProgramSiteIds.has(site._id))
        : [...this._allList];
    }
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters[this.key] = this._selectedList.map((site: Site) => site._id);
  }
}

export class UserSearchFilter extends AbstractBaseFilter {
  key: UserSearchFilterKey;
  type: FilterType;
  translateService: TranslateService;

  private _selectedUsers: User[];

  constructor(
    key: UserSearchFilterKey,
    type: FilterType,
    translateService: TranslateService,
    title: string,
  ) {
    super();
    this.key = key;
    this.type = type;
    this.translateService = translateService;
    this.title = title;
    this._selectedUsers = [];
  }

  get selectedUsers(): User[] {
    return this._selectedUsers;
  }

  isApplied(): boolean {
    return !!this._selectedUsers.length;
  }

  handleFilterSelection(filterValue?: User[]): boolean {
    this._selectedUsers = filterValue ?? [];
    return this.isApplied();
  }

  handleClearFilter(): void {
    this._selectedUsers = [];
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters[this.key] = this._selectedUsers.map((user: User) => user._id ?? user.email);
  }

  getFilterPillText(): string {
    return getMultiSelectFilterPillText(
      this._selectedUsers.length,
      this._selectedUsers[0].name ?? this._selectedUsers[0].email,
      this.translateService,
    );
  }
}

abstract class AbstractCheckboxFilter extends AbstractBaseFilter {
  type = FilterType.Checkbox;
  readonly key: FilterKey;
  protected _isChecked = false;
  protected _label: string;
  protected _pillText: string;

  constructor(
    protected translateService: TranslateService,
    key: FilterKey,
    title: string,
    label: string,
  ) {
    super();
    this.key = key;
    this.title = title;
    this._label = label;
    this._pillText = this.translateService.instant('Yes');
  }

  get label(): string {
    return this._label;
  }

  get isChecked(): boolean {
    return this._isChecked;
  }

  set isChecked(value: boolean) {
    this._isChecked = value;
  }

  isApplied(): boolean {
    return this.isChecked;
  }

  handleFilterSelection(filterValue: boolean): boolean {
    this.isChecked = filterValue;
    return this.isApplied();
  }

  handleClearFilter(): void {
    this.isChecked = false;
  }

  getFilterPillText(): string {
    return this._pillText;
  }
}

export class OwnerFilter extends AbstractCheckboxFilter {
  // readonly ensures owner ID remains immutable throughout filter lifecycle
  private readonly _ownerId: string;

  constructor(translateService: TranslateService, title: string, ownerId: string, label: string) {
    super(translateService, DefaultFilterKey.OwnerFilter, title, label);
    this._ownerId = ownerId;
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.owner = this._ownerId;
  }
}

export class ArchivedFilter extends AbstractCheckboxFilter {
  constructor(translateService: TranslateService, title: string, label: string) {
    super(translateService, DefaultFilterKey.ArchiveStatusFilter, title, label);
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.archiveStatus = Collaboration.Spaces.ArchiveStatus.ARCHIVED;
  }

  getFilterPillTag(): string {
    return this.translateService.instant('IS ARCHIVED');
  }
}

export class EventsAtSameTimeFilter extends AbstractCheckboxFilter {
  event: Event;

  constructor(translateService: TranslateService, title: string, label: string, event: Event) {
    super(translateService, DefaultFilterKey.EventsAtSameTimeFilter, title, label);
    this.event = event;
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.timeRangeOverlap = new DateRange(this.event.start_time, this.event.end_time!);
  }
}

export class HasCallsFilter extends AbstractCheckboxFilter {
  constructor(translateService: TranslateService, title: string, label: string) {
    super(translateService, DefaultFilterKey.HasCallsFilter, title, label);
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.hasCalls = true;
  }
}

export class HasRecordingFilter extends AbstractCheckboxFilter {
  constructor(translateService: TranslateService, title: string, label: string) {
    super(translateService, DefaultFilterKey.HasRecordingFilter, title, label);
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.hasRecording = true;
  }
}

export class DateRangeFilter extends AbstractBaseFilter {
  key: DefaultFilterKey.DateCreatedFilter | DefaultFilterKey.DateUpdatedFilter;
  translateService: TranslateService;
  type = FilterType.Date;

  private _dateRange?: DateRange<Date | null>;

  constructor(
    key: DefaultFilterKey.DateCreatedFilter | DefaultFilterKey.DateUpdatedFilter,
    translateService: TranslateService,
    title: string,
  ) {
    super();
    this.key = key;
    this.translateService = translateService;
    this.title = title;
  }

  get dateRange(): DateRange<Date | null> | undefined {
    return this._dateRange;
  }

  isApplied(): boolean {
    return !!this._dateRange;
  }

  handleFilterSelection(filterValue: DatepickerRange): boolean {
    this._dateRange = filterValue.range;
    return this.isApplied();
  }

  handleClearFilter(): void {
    this._dateRange = undefined;
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters[this.key] = this._dateRange;
  }

  getFilterPillText(): string {
    return new DateRangePickerSelectionNoLabelPipe(this.translateService).transform(
      this._dateRange,
    );
  }
}

export class AttributesSearchFilter extends AbstractBaseFilter {
  key: FilterKey;
  type: FilterType;
  translateService: TranslateService;
  _customAttribute: CustomAttribute;
  _selectedFilterAttributeValues: AttributeValue[];

  constructor(translateService: TranslateService, customAttribute: CustomAttribute) {
    super();
    this.translateService = translateService;
    this.key = customAttribute.attribute_key._id;
    this.type = FilterType.MultiSelect;
    this.title = customAttribute.attribute_key.value.toUpperCase();
    this._selectedFilterAttributeValues = [];
    this._customAttribute = customAttribute;
  }

  get addableList(): AttributeValue[] {
    return this._customAttribute.attribute_value;
  }

  get selectedList(): AttributeValue[] {
    return this._selectedFilterAttributeValues;
  }

  isApplied(): boolean {
    return !!this._selectedFilterAttributeValues.length;
  }

  handleFilterSelection(filterValue: AttributeValue[]): boolean {
    this._selectedFilterAttributeValues = filterValue;
    return this.isApplied();
  }

  handleClearFilter(): void {
    this._selectedFilterAttributeValues = [];
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    (apiFilters.customAttributes ??= []).push(
      ...this._selectedFilterAttributeValues.map((attribute) => `${this.key}:${attribute._id}`),
    );
  }

  getFilterPillText(): string {
    return getMultiSelectFilterPillText(
      this._selectedFilterAttributeValues.length,
      this._selectedFilterAttributeValues[0].value,
      this.translateService,
    );
  }
}

export class RoleFilter extends AbstractMultiSelectFilter<PersonaEntry> {
  key: FilterKey;
  type: FilterType.Role;

  constructor(
    translateService: TranslateService,
    title: string,
    selectedList: PersonaEntry[],
    addableList?: PersonaEntry[],
  ) {
    super(translateService);
    this.key = DefaultFilterKey.RolesFilter;
    this.type = FilterType.Role;
    this.title = title;
    this._selectedList = this.mapPersonaListToTranslatedValues(selectedList ?? []);
    this._addableList = this.mapPersonaListToTranslatedValues(addableList ?? []);
  }

  mapPersonaListToTranslatedValues(list: PersonaEntry[]) {
    return list.map((persona) => ({
      _id: persona._id,
      value: this.translateService.instant(persona.value),
    }));
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.roles = this.selectedList.map((role) => role._id);
  }

  getFilterPillTag(): string {
    return this.translateService.instant(this._selectedList.length === 1 ? 'ROLE' : 'ROLES');
  }
}

export class SpaceFilter extends AbstractBaseFilter {
  key: FilterKey;
  type: FilterType.SpaceSearch;
  _selectedList: SpaceItem[];

  constructor(
    private translateService: TranslateService,
    title: string,
    selectedList: SpaceItem[],
  ) {
    super();
    this.key = DefaultFilterKey.SpacesFilter;
    this.type = FilterType.SpaceSearch;
    this.title = title;
    this._selectedList = selectedList ?? [];
  }

  get selectedList(): SpaceItem[] {
    return this._selectedList;
  }

  isApplied(): boolean {
    return !!this._selectedList.length;
  }

  handleFilterSelection(filterValue: SpaceItem[]): boolean {
    this._selectedList = filterValue;
    return this.isApplied();
  }

  handleClearFilter(): void {
    this._selectedList = [];
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.spaces = this.selectedList.map((space) => space._id);
  }

  getFilterPillTag(): string {
    return this._selectedList.length === 1
      ? this.translateService.instant('SPACE')
      : this.translateService.instant('SPACES');
  }

  getFilterPillText(): string {
    return getMultiSelectFilterPillText(
      this.selectedList.length,
      this.selectedList[0].title,
      this.translateService,
    );
  }
}

export class ProgramSearchFilter extends AbstractMultiSelectFilter<Program> {
  key = DefaultFilterKey.ProgramsFilter;
  type = FilterType.MultiSelect;
  valuePropName: string = 'name';

  constructor(
    translateService: TranslateService,
    title: string,
    programs?: Program[],
    allPrograms?: Program[],
  ) {
    super(translateService);
    this.title = title;
    this._allList = allPrograms ?? [];
    this._selectedList = programs ?? [];
    this._addableList = [...this._allList];
  }

  override onLinkedFilterChanged(sourceFilter: FilterData): void {
    if (sourceFilter.key === DefaultFilterKey.SitesFilter) {
      const siteFilter = sourceFilter as SiteSearchFilter;
      const selectedSiteIds = new Set(siteFilter.selectedList.map((site) => site._id));
      // Note that no selected sites means the site filter was cleared, so reset this filter's options to show all unfiltered options again.
      this._addableList = selectedSiteIds.size
        ? this._allList.filter((program) => selectedSiteIds.has(program.siteId))
        : [...this._allList];
    }
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.programs = this.selectedList.map((program) => program._id);
  }

  getFilterPillTag(): string {
    return this.translateService.instant(this.selectedList.length === 1 ? 'PROGRAM' : 'PROGRAMS');
  }
}

interface FileFilterOption {
  _id: string;
  value: string;
}

export class FileTypeFilter extends AbstractMultiSelectFilter<FileFilterOption> {
  key = DefaultFilterKey.FileTypesFilter;
  type = FilterType.MultiSelect;

  constructor(translateService: TranslateService, title: string) {
    super(translateService);
    this.title = title;
    this._addableList = [
      { _id: ResourceItemModel.IMAGE, value: this.translateService.instant('Image') },
      { _id: ResourceItemModel.DOCUMENT, value: this.translateService.instant('Document') },
      { _id: ResourceItemModel.VIDEO, value: this.translateService.instant('Video') },
    ];
    this._allList = [...this._addableList];
  }

  convertToAPIFormat(apiFilters: IFilterAPI): void {
    apiFilters.type = this.selectedList.map((option) => option._id);
  }

  getFilterPillTag(): string {
    return this.translateService.instant(this.selectedList.length === 1 ? 'TYPE' : 'TYPES');
  }
}

export type FilterData =
  | SiteSearchFilter
  | UserSearchFilter
  | OwnerFilter
  | DateRangeFilter
  | AttributesSearchFilter
  | RoleFilter
  | ArchivedFilter
  | EventsAtSameTimeFilter
  | HasCallsFilter
  | HasRecordingFilter
  | SpaceFilter
  | ProgramSearchFilter
  | FileTypeFilter;
